/**
 * Funções para gerenciar perfis de usuários
 * CRUD de profiles no Supabase
 */

import { supabase } from './supabase'
import { Profile, ProfileUpdate } from './types'
import { getDeviceInfo } from './deviceDetection'

/**
 * Cria ou atualiza o perfil do usuário
 */
export async function upsertProfile(userId: string, data: ProfileUpdate) {
  // First, try to update existing profile
  const { data: updated, error: updateError } = await supabase
    .from('profiles')
    .update({
      ...data,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .select()
    .single()

  // If profile exists, return it
  if (updated) {
    return updated as Profile
  }

  // If no profile exists (update returns null), create new one
  if (updateError?.code === 'PGRST116' || !updated) {
    const { data: profile, error: insertError } = await supabase
      .from('profiles')
      .insert({
        user_id: userId,
        ...data,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single()

    if (insertError) throw insertError
    return profile as Profile
  }

  // Re-throw other errors
  if (updateError) throw updateError
  return updated as Profile
}

/**
 * Busca o perfil do usuário
 */
export async function getProfile(userId: string) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single()

  if (error && error.code !== 'PGRST116') throw error
  return data as Profile | null
}

/**
 * Atualiza o perfil do usuário
 */
export async function updateProfile(userId: string, updates: ProfileUpdate) {
  const { data, error } = await supabase
    .from('profiles')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .select()
    .single()

  if (error) throw error
  return data as Profile
}

/**
 * Atualiza informações de último acesso
 */
export async function updateLastAccess(userId: string) {
  const deviceInfo = getDeviceInfo()
  
  const { error } = await supabase
    .from('profiles')
    .update(deviceInfo)
    .eq('user_id', userId)

  if (error) throw error
}

/**
 * Deleta o perfil do usuário
 */
export async function deleteProfile(userId: string) {
  const { error } = await supabase
    .from('profiles')
    .delete()
    .eq('user_id', userId)

  if (error) throw error
}

/**
 * Cria perfil inicial com informações básicas
 */
export async function createInitialProfile(
  userId: string,
  fullName?: string,
  phone?: string,
  facebook?: string,
  instagram?: string,
  tiktok?: string,
  email?: string
) {
  const deviceInfo = getDeviceInfo()
  
  // Criar o perfil diretamente (não há mais trigger automático)
  const { data, error } = await supabase
    .from('profiles')
    .insert({
      user_id: userId,
      full_name: fullName || null,
      email: email || null, // Email será preenchido automaticamente pelo trigger se não fornecido
      phone: phone || null,
      whatsapp: phone || null, // WhatsApp usa o mesmo número do celular
      facebook: facebook || null,
      instagram: instagram || null,
      tiktok: tiktok || null,
      theme: 'light',
      ...deviceInfo,
    })
    .select()
    .single()

  if (error) throw error
  return data as Profile
}
